import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { FileDropZone } from "../../components/FileDropZone";
import { PortalPage, Section, Field } from "../../components/portal/PortalPage";
import { PropertyPicker } from "../../components/portal/PropertyPicker";
import { OnBehalfSelect } from "../../components/portal/FamilyFilter";
import { CATEGORIES, titleCase } from "../../components/portal/portalUtils";
import { Kanban, Blueprint, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

import DateInput from "../../components/DateInput";
const EMPTY = {
  title: "", scope: "", category: "planned_works", priority: "medium", desired_start_date: "", requested_completion_date: "",
  estimated_budget: "", property_id: "", property_address: "", site_contact_name: "", site_contact_phone: "", attachments: [],
};

export default function ClientProjectFormPage() {
  const [params] = useSearchParams();
  const [f, setF] = useState({ ...EMPTY, on_behalf_client_id: params.get("for") || "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Give your project a name");
    if (!f.scope.trim()) return toast.error("Describe the scope of works");
    if (!f.property_id && !f.property_address.trim()) return toast.error("Pick a property or enter the address");
    setSaving(true);
    try {
      const { data } = await API.post("/client-hub/projects", { ...f, estimated_budget: f.estimated_budget ? parseFloat(f.estimated_budget) : 0 });
      toast.success(`Project request ${data.reference} submitted — we'll review and quote it`);
      navigate(`/portal/requests/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not submit");
    } finally { setSaving(false); }
  };

  return (
    <PortalPage
      title="Propose a Project"
      subtitle="For larger, multi-stage works — refurbishments, fit-outs, planned programmes. We'll scope it, quote it and, once you approve, run it as a live project you can follow task by task."
      back={{ to: "/portal/projects", label: "Projects" }}
      testid="project-form-page"
    >
      <form onSubmit={submit} className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Section title={<span className="flex items-center gap-1.5"><Kanban size={14} /> The project</span>}>
            <div className="space-y-4">
              <Field label="Project name *">
                <Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Ground floor office refurbishment" data-testid="project-title" />
              </Field>
              <Field label="Scope of works *" hint="Describe what you want delivered — areas, finishes, phases, constraints (occupied building, out-of-hours working, etc.).">
                <Textarea rows={7} value={f.scope} onChange={(e) => set("scope", e.target.value)} data-testid="project-scope" />
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Type of works">
                  <Select value={f.category} onValueChange={(v) => set("category", v)}>
                    <SelectTrigger data-testid="project-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{titleCase(c)}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Priority">
                  <Select value={f.priority} onValueChange={(v) => set("priority", v)}>
                    <SelectTrigger data-testid="project-priority"><SelectValue /></SelectTrigger>
                    <SelectContent>{["low", "medium", "high", "urgent"].map((p) => <SelectItem key={p} value={p}>{titleCase(p)}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <Field label="Desired start">
                  <DateInput value={f.desired_start_date} onChange={(e) => set("desired_start_date", e.target.value)} data-testid="project-start-date" />
                </Field>
                <Field label="Target completion">
                  <DateInput value={f.requested_completion_date} onChange={(e) => set("requested_completion_date", e.target.value)} data-testid="project-target-date" />
                </Field>
                <Field label="Indicative budget (£, optional)">
                  <Input type="number" min="0" step="100" value={f.estimated_budget} onChange={(e) => set("estimated_budget", e.target.value)} placeholder="e.g. 25000" data-testid="project-budget" />
                </Field>
              </div>
            </div>
          </Section>

          <Section title="Where?">
            <div className="space-y-4">
              <OnBehalfSelect value={f.on_behalf_client_id || ""} onChange={(v) => setF((p) => ({ ...p, on_behalf_client_id: v, property_id: "" }))} testid="project-on-behalf" />
              <Field label="Property"><PropertyPicker value={f.property_id} clientId={f.on_behalf_client_id || ""} onChange={(id, prop) => setF((p) => ({ ...p, property_id: id, on_behalf_client_id: prop?.client_id && prop.for_client ? prop.client_id : p.on_behalf_client_id }))} /></Field>
              {!f.property_id && <Field label="Property address *"><Input value={f.property_address} onChange={(e) => set("property_address", e.target.value)} placeholder="Full address including postcode" data-testid="project-address" /></Field>}
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="On-site contact name"><Input value={f.site_contact_name} onChange={(e) => set("site_contact_name", e.target.value)} data-testid="project-site-name" /></Field>
                <Field label="On-site contact phone"><Input value={f.site_contact_phone} onChange={(e) => set("site_contact_phone", e.target.value)} data-testid="project-site-phone" /></Field>
              </div>
            </div>
          </Section>

          <Section title={<span className="flex items-center gap-1.5"><Blueprint size={14} /> Plans, drawings & photos</span>}>
            <p className="text-xs text-muted-foreground mb-3">Attach floor plans, drawings, specifications or photos of the space. Up to 15 files, 20MB each.</p>
            <FileDropZone files={f.attachments} onFilesChange={(files) => set("attachments", files)} folder="client-projects" maxFiles={15} />
          </Section>
        </div>

        <div className="space-y-6">
          <Section title={<span className="flex items-center gap-1.5"><Info size={14} className="text-primary" /> What happens next</span>} testid="project-how-it-works">
            <ol className="space-y-2.5 text-xs text-muted-foreground list-decimal pl-4">
              <li><strong className="text-foreground">We review</strong> your scope and may come back with questions via comments.</li>
              <li><strong className="text-foreground">We quote</strong> the works — you'll see the quote here and can approve or decline it.</li>
              <li><strong className="text-foreground">Project goes live</strong> — tasks, milestones and progress appear in your Projects section.</li>
              <li><strong className="text-foreground">Follow along</strong> — photos, sign-offs and completion documents as each stage is finished.</li>
            </ol>
          </Section>
          <div className="border border-border rounded-lg bg-card p-4 space-y-3">
            <Button type="submit" className="w-full" disabled={saving} data-testid="project-submit">{saving ? "Submitting…" : "Submit Project Request"}</Button>
            <Button type="button" variant="ghost" className="w-full" onClick={() => navigate(-1)} data-testid="project-cancel">Cancel</Button>
            <p className="text-[11px] text-muted-foreground text-center">You'll receive an acknowledgement and can track the request straight away.</p>
          </div>
        </div>
      </form>
    </PortalPage>
  );
}
